import React from 'react';
import _ from 'lodash';
import { useAsyncEffect } from 'rooks';
import { SummaryHistogram } from '@/summaryReport/summaryHistogram.organism';
import { StatisticsSummaryItem } from '@/summaryReport/summaryData.types';
import { StatisticsSummaryBoxPlot } from '@/summaryReport/summaryReportBoxPlot.organism';
import { useTranslation } from 'react-i18next';
import { SummaryReportTable } from '@/summaryReport/summaryReportTable';
import { SummaryReportToolbar } from '@/summaryReport/summaryReportToolbar';
import { useFluxPath } from '@/core/hooks/useFluxPath.hook';
import { Icon } from '@seeqdev/qomponents';
import { sqSummaryReportStore } from '@/core/core.stores';
import classNames from 'classnames';
import { fetchStatsData, removeItems } from '@/summaryReport/summaryReport.actions';
import { NoStatisticsSummaryView } from '@/summaryReport/NoItemsContainer';
import { ITEM_DATA_STATUS } from '@/trendData/trendData.constants';
import { isPresentationWorkbookMode } from '@/utilities/utilities';
import { headlessRenderMode } from '@/services/headlessCapture.utilities';
import { provideVisualizationData } from '@/annotation/ckEditorPlugins/components/content.utilities';
import { Visualization } from '@/annotation/ckEditorPlugins/components/content.utilities.constants';
import { loadToolForEdit } from '@/toolSelection/investigate.actions';

export interface SummaryReportViewerProps {
  items: StatisticsSummaryItem[];
  isOverlaid: boolean;
  status: ITEM_DATA_STATUS;
  isContent?: boolean;
}

export const SummaryReportViewerWrapper: React.FunctionComponent = () => {
  const items = useFluxPath(sqSummaryReportStore, () => sqSummaryReportStore.items);
  const isOverlaid = useFluxPath(sqSummaryReportStore, () => sqSummaryReportStore.isItemsOverlaid);
  const status = useFluxPath(sqSummaryReportStore, () => sqSummaryReportStore.status);

  useAsyncEffect(async () => {
    await fetchStatsData(items);
  }, []);

  const props: SummaryReportViewerProps = {
    items,
    isOverlaid,
    status,
  };
  return <SummaryReportViewer {...props} />;
};

export const SummaryReportViewer: React.FunctionComponent<SummaryReportViewerProps> = (props) => {
  const { items, isOverlaid, status, isContent } = props;
  const isLoaded = status === ITEM_DATA_STATUS.PRESENT;
  const { t } = useTranslation();
  const hideToolbar = isPresentationWorkbookMode() || isContent;

  // Provide visualization data for interactive content
  if (headlessRenderMode()) {
    provideVisualizationData({
      visualization: Visualization.STATISTICS_SUMMARY,
      ...props,
    });
  }

  const renderStatisticsSummary = (stats: StatisticsSummaryItem | StatisticsSummaryItem[]) => {
    const items = Array.isArray(stats) ? stats : [stats];

    return (
      <div
        className={classNames(
          'flexRowContainer',
          'm5',
          'p10',
          'statsSummaryContent',
          'backgroundColorWhite',
          'height-fit-content',
          isOverlaid ? 'max-width-1000 min-width-600' : 'max-width-500',
        )}
        key={items[0].id}>
        <div className="flexColumnContainer flexSpaceBetween">
          <p className="text-bold m0 mt5">
            {Array.isArray(stats) ? items.map((item) => item.name).join(', ') : items[0].name}
          </p>
          <div className="flexColumnContainer">
            {!hideToolbar && items.length === 1 && (
              <Icon icon="fa-pencil" type="theme" extraClassNames="mr7" onClick={() => loadToolForEdit(items[0].id)} />
            )}
            {!hideToolbar && <Icon icon="fa-close" type="theme" onClick={() => removeItems(items)} />}
          </div>
        </div>
        <div
          className={classNames(
            isOverlaid ? 'flexColumnContainer' : 'flexRowContainer',
            'flexCenter flexSpaceBetween',
          )}>
          <div className={classNames(isOverlaid ? 'width-50percent' : 'width-maximum')}>
            <SummaryHistogram items={items} title={t('INVESTIGATE_TOOLS.SUMMARY_REPORT.HISTOGRAM')} />
          </div>
          <div className={classNames(isOverlaid ? 'width-50percent' : 'width-maximum')}>
            <StatisticsSummaryBoxPlot items={items} title={t('INVESTIGATE_TOOLS.SUMMARY_REPORT.BOXPLOT')} />
          </div>
        </div>
        <SummaryReportTable items={items} />
      </div>
    );
  };

  const renderContent = () => {
    if (_.isEmpty(items)) {
      return (
        <div className="flexRowContainer flexCenter flexFill">
          <NoStatisticsSummaryView />
        </div>
      );
    }

    if (!isOverlaid) {
      return items.map((item) => renderStatisticsSummary(item));
    }

    return renderStatisticsSummary(items);
  };

  return (
    <>
      {isLoaded && (
        <div className="resizablePanelContainer flexRowContainer">
          {!hideToolbar && (
            <div className="flexColumnContainer flexSpaceBetween toolbar pl10">
              <SummaryReportToolbar />
            </div>
          )}
          <div className="flexColumnContainer align-center height-maximum overflow-auto backgroundColorOffWhite">
            {renderContent()}
          </div>
        </div>
      )}
    </>
  );
};
